import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  Entitlement,
  Entitlements,
  Environment,
  Organization,
  ResponseState,
  Tenant,
  UserData
} from '@models';
import { EntitlementsService, OrgClientService, UserDetailsService } from '@services';
import { BehaviorSubject, forkJoin, Observable, of, switchMap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { LoggerService } from '../logger/logger.service';

@Injectable({ providedIn: 'root' })
export class UserDataService {
  private entitlementsService: EntitlementsService = inject(EntitlementsService);
  private userDetailsService: UserDetailsService = inject(UserDetailsService);
  private orgClientService: OrgClientService = inject(OrgClientService);
  private logger = inject(LoggerService).setContext('UserDataService');
  private username: string | null = null;
  private userOrgIds: string[] | null = null;
  private standardClientNames: string[] | null = null;
  private readonly requiredEntitlementName = 'CRG Sponsor Portal';
  private userDataLoaded$ = new BehaviorSubject<boolean>(false);
  isUserDataLoaded$: Observable<boolean> = this.userDataLoaded$.asObservable();

  constructor() {
    if (this.userDataExists()) {
      this.userDataLoaded$.next(true);
    }
  }

  getUserData(): Observable<UserData> {
    if (this.userDataLoaded$.value) {
      // Already loaded, return current data
      return of({
        username: this.getUsername(),
        userOrgIds: this.getUserOrgIds(),
        standardClientNames: this.getStandardClientNames()
      } as UserData);
    }
    const entitlements$: Observable<ResponseState<Entitlements>> = this.entitlementsService
      .getUserEntitlements()
      .pipe(
        map((data) => ({ status: 'success', data }) as const),
        catchError((err: HttpErrorResponse) => {
          this.logger.warn('Error getting user entitlements:', err);
          if (err.status == 401) {
            return of({ status: 'error', errorCode: err.status } as const);
          }

          return of({ status: 'success', data: this.getEmptyEntitlements() } as const);
        })
      );

    const userDetails$: Observable<ResponseState<any>> = this.userDetailsService
      .getUserDetails()
      .pipe(
        map((data) => ({ status: 'success', data }) as const),
        catchError((err: HttpErrorResponse) => {
          this.logger.warn('Error getting user details:', err);
          if (err.status == 401) {
            return of({ status: 'error', errorCode: err.status } as const);
          }
          return of({ status: 'success', data: {} } as const);
        })
      );
    return forkJoin({
      entitlementsResponse: entitlements$,
      userDetailsResponse: userDetails$
    }).pipe(
      switchMap(({ entitlementsResponse, userDetailsResponse }) => {
        if (entitlementsResponse.status == 'error' || userDetailsResponse.status == 'error') {
          sessionStorage.setItem('alreadyRedirected', 'false');
          throw new Error('UserNotLoggedIn');
        }

        // Check if user has "CRG Sponsor Portal" entitlement in any organization
        const hasPortalAccess = this.checkPortalAccess(
          entitlementsResponse.data.organizations || []
        );

        if (!hasPortalAccess) {
          this.logger.error(`User does not have ${this.requiredEntitlementName} entitlement`);
          throw new Error('UserNotAuthorizedForPortal');
        }

        // Get all organization IDs that the user has access to
        const userOrganizations = entitlementsResponse.data.organizations.map((organization) =>
          organization.id.toString()
        );

        this.setUserOrgIds(userOrganizations);
        this.setUsername(userDetailsResponse.data.username);
        this.setPrincipalId(userDetailsResponse.data.principalId);

        if (!this.username || !this.userOrgIds) {
          this.logger.error('Username or User Org IDs were not able to be fetched.');
          throw new Error('UsernameOrUserOrgIdsMissing');
        }

        return this.orgClientService.getStandardClientNames(this.username, this.userOrgIds).pipe(
          map((clientResponse) => {
            this.setStandardClientNames(clientResponse.standardClientNames);
            this.setUserDataAsLoaded();
            this.userDataLoaded$.next(true);

            return {
              username: this.getUsername(),
              userOrgIds: this.getUserOrgIds(),
              standardClientNames: this.getStandardClientNames()
            } as UserData;
          }),
          catchError((err) => {
            this.logger.error('Error fetching client names:', err);
            throw new Error('ClientNamesFetchError');
          })
        );
      }),
      catchError((err) => {
        this.logger.error('Error fetching user data:', err);
        throw err;
      })
    );
  }

  getUsername(): string | null {
    return this.username || sessionStorage.getItem('username');
  }

  getUserOrgIds(): string[] | null {
    return this.userOrgIds || JSON.parse(sessionStorage.getItem('userOrgIds') || 'null');
  }

  getPrincipalId(): string | null {
    return sessionStorage.getItem('principalId');
  }

  getStandardClientNames(): string[] | null {
    return (
      this.standardClientNames ||
      JSON.parse(sessionStorage.getItem('standardClientNames') || 'null')
    );
  }

  isUserDataAlreadyLoaded(): boolean {
    return sessionStorage.getItem('userDataAlreadyLoaded') === 'true';
  }

  clearUserData(): void {
    sessionStorage.clear();
    this.username = null;
    this.userOrgIds = null;
    this.standardClientNames = null;
    this.userDataLoaded$.next(false);
  }

  userDataExists(): boolean {
    /**
     * All user data should exists and different from null
     */
    return !!(this.getUsername() && this.getUserOrgIds() && this.getStandardClientNames());
  }

  private setUsername(username: string | null): void {
    this.username = username;
    if (this.username) {
      sessionStorage.setItem('username', this.username);
    } else {
      sessionStorage.removeItem('username');
    }
  }

  private setUserOrgIds(orgIds: string[] | null): void {
    this.userOrgIds = orgIds;
    if (this.userOrgIds) {
      sessionStorage.setItem('userOrgIds', JSON.stringify(this.userOrgIds));
    } else {
      sessionStorage.removeItem('userOrgIds');
    }
  }

  private setPrincipalId(principalId: string): void {
    sessionStorage.setItem('principalId', principalId);
  }

  private setStandardClientNames(standardClientNames: string[]): void {
    this.standardClientNames = standardClientNames;
    sessionStorage.setItem('standardClientNames', JSON.stringify(this.standardClientNames));
  }

  private setUserDataAsLoaded(): void {
    sessionStorage.setItem('userDataAlreadyLoaded', 'true');
  }

  private checkPortalAccess(organizations: Organization[]): boolean {
    return organizations.some((org) =>
      org.environments.some((env: Environment) =>
        env.tenants.some((tenant: Tenant) =>
          tenant.entitlements.some(
            (entitlement: Entitlement) => entitlement.name === this.requiredEntitlementName
          )
        )
      )
    );
  }

  private getEmptyEntitlements(): Entitlements {
    return {
      messageName: '',
      organizations: []
    };
  }
}
